/**
 * features/administration/mock.ts — deterministic fixtures for every Administration
 * read (Story UI-U6, Task 5). Used in mock data-mode (vitest default + dev) so each
 * screen renders standalone with no backend. Synthetic data only — NO real PII.
 *
 * [Source: apps/web/src/features/carriers hooks — mock fixture convention]
 */

import {
  CHANNEL_CATALOG,
  PORTAL_FEATURE_KEYS,
  VISIBILITY_CATALOG,
  FIELD_PERMISSION_CATALOG,
  PERMISSION_CATALOG,
  PERMISSION_CATEGORIES,
  BASIC_BASELINE_KEYS,
  type AdminMembersResponse,
  type ChannelConfigDto,
  type ChannelMatrixEntryDto,
  type PortalConfigDto,
  type AuditListResponse,
  type HealthStatus,
  type VisibilityKeyDefinition,
  type FieldPermissionKeyDefinition,
  type RoleFieldPermissionsResponse,
  type ControlBacklogListResponse,
  type ProcessReminderConfigResponseDto,
  type MahnwesenProcessConfigDto,
  type TextTemplateDto,
  MAHNWESEN_OPTIONAL_THIRD_FRIST_DAY,
  mahnwesenProcessDefaults,
  resolveTextTemplateDefault,
  textTemplateListMatrix,
  PROCESS_REMINDER_PROCESS_TYPES,
  PROCESS_REMINDER_PROCESS_LABELS,
  PROCESS_REMINDER_DEFAULT_LEVEL_DAYS,
  PROCESS_REMINDER_DEFAULT_KANAL,
  PROCESS_REMINDER_DEFAULT_L3_ACTION,
  PROCESS_REMINDER_DEFAULT_TEMPLATE,
  regelwerkSeedRows,
  pickLabel,
  type RegelwerkListResponse,
  type RegelwerkSollDeckungItem,
  type RegelwerkAttributSchemaItem,
} from '@brokly/shared';
import type {
  RoleDto,
  CatalogResponseDto,
  UserRolesDto,
  FunktionsgruppeDto,
  FunktionsgruppeDetailDto,
  KundenteamListResponse,
  KundenteamDetailDto,
  ToleranceConfigEntryDto,
  ConfidencePolicyEntryDto,
  GroupRoutingEntryDto,
  TenantConfigDto,
  AvbDocumentResponse,
  PortalGrantAdminItem,
} from './types';
import type { PortalAccessResponse } from '../../lib/api-client';

// ── Ids (stable, synthetic) ───────────────────────────────────────────────────
export const MOCK_USER_A = 'aa000001-0000-0000-0000-000000000001';
export const MOCK_USER_B = 'aa000002-0000-0000-0000-000000000002';
export const MOCK_ROLE_ADMIN = 'aa000010-0000-0000-0000-000000000010';
export const MOCK_ROLE_BASIC = 'aa000011-0000-0000-0000-000000000011';
export const MOCK_ROLE_FINANCE = 'aa000012-0000-0000-0000-000000000012';
export const MOCK_GRUPPE_A = 'aa000013-0000-0000-0000-000000000013';
export const MOCK_TEAM_A = 'aa000020-0000-0000-0000-000000000020';

// ── Members ───────────────────────────────────────────────────────────────────
export const MOCK_MEMBERS: AdminMembersResponse = {
  members: [
    {
      user_id: MOCK_USER_A,
      display_name: 'Lara Meier',
      email: 'lara.meier@brokerfirma-beispiel.ch',
      membership_role: 'admin',
      role_names: ['Administrator'],
      created_at: '2026-01-15T08:00:00.000Z',
    },
    {
      user_id: MOCK_USER_B,
      display_name: 'Tomas Bianchi',
      email: 'tomas.bianchi@brokerfirma-beispiel.ch',
      membership_role: 'user',
      role_names: ['Broker', 'Finance'],
      created_at: '2026-02-20T08:00:00.000Z',
    },
  ],
  invitations: [
    {
      id: 'aa000030-0000-0000-0000-000000000030',
      email: 'neu.kollege@brokerfirma-beispiel.ch',
      display_name: 'Nadia Kollege',
      role_id: null,
      state: 'invited',
      created_at: '2026-03-05T08:00:00.000Z',
    },
  ],
};

// ── Roles + catalog ─────────────────────────────────────────────────────────
const now = '2026-03-01T00:00:00.000Z';
export const MOCK_ROLES: RoleDto[] = [
  {
    id: MOCK_ROLE_ADMIN,
    name: 'Administrator',
    is_system: true,
    is_wildcard: true,
    kind: 'rolle',
    ist_administrationsrolle: false,
    description: 'Voller Zugriff auf alle Funktionen.',
    permission_keys: [],
    created_at: now,
    updated_at: now,
  },
  {
    id: MOCK_ROLE_BASIC,
    name: 'Broker',
    is_system: true,
    is_wildcard: false,
    kind: 'rolle',
    ist_administrationsrolle: false,
    description: 'Standardrolle für Brokerinnen und Broker.',
    permission_keys: [...BASIC_BASELINE_KEYS],
    created_at: now,
    updated_at: now,
  },
  {
    id: MOCK_ROLE_FINANCE,
    name: 'Finance',
    is_system: false,
    is_wildcard: false,
    kind: 'gruppe',
    ist_administrationsrolle: false,
    description: 'Zusätzliche Finanz-Einsicht (Courtage, Beträge).',
    permission_keys: ['commission.read', 'commission.read_amounts', 'invoice_check.read_amounts'],
    created_at: now,
    updated_at: now,
  },
];

export const MOCK_CATALOG: CatalogResponseDto = {
  categories: PERMISSION_CATEGORIES.map((c) => ({
    key: c.key,
    label: c.label,
    permissions: PERMISSION_CATALOG.filter((p) => p.category === c.key).map((p) => ({
      key: p.key,
      op: p.op,
      label: p.label,
      description: p.description,
    })),
  })),
  roles: MOCK_ROLES.map((r) => ({
    id: r.id,
    name: r.name,
    is_system: r.is_system,
    is_wildcard: r.is_wildcard,
    kind: r.kind,
    ist_administrationsrolle: r.ist_administrationsrolle,
    selected_keys: r.is_wildcard ? [] : r.permission_keys,
  })),
};

export const MOCK_USER_ROLES: Record<string, UserRolesDto> = {
  [MOCK_USER_A]: {
    user_id: MOCK_USER_A,
    roles: [{ id: MOCK_ROLE_ADMIN, name: 'Administrator', is_system: true, is_wildcard: true, kind: 'rolle' }],
  },
  [MOCK_USER_B]: {
    user_id: MOCK_USER_B,
    roles: [
      { id: MOCK_ROLE_BASIC, name: 'Broker', is_system: true, is_wildcard: false, kind: 'rolle' },
      { id: MOCK_ROLE_FINANCE, name: 'Finance', is_system: false, is_wildcard: false, kind: 'gruppe' },
    ],
  },
};

// Mock mode must agree with live: the api omits `wire_ready` catalog entries (the ones
// with no backing data yet) from both catalogs, so the mock catalogs drop them too. The
// annotated consts widen the `as const` catalogs, on whose literal element types the
// optional flag is not visible (TS2339 on a direct .filter).
const VISIBILITY_ENTRIES: readonly VisibilityKeyDefinition[] = VISIBILITY_CATALOG;
const FIELD_CATALOG_ENTRIES: readonly FieldPermissionKeyDefinition[] = FIELD_PERMISSION_CATALOG;

// ── Visibility ────────────────────────────────────────────────────────────────
export const MOCK_VISIBILITY_CATALOG: VisibilityKeyDefinition[] = VISIBILITY_ENTRIES.filter(
  (v) => v.wire_ready !== true,
);

// ── Feldrechte (WS1 §5.2) ─────────────────────────────────────────────────────
export const MOCK_FIELD_CATALOG: FieldPermissionKeyDefinition[] = FIELD_CATALOG_ENTRIES.filter(
  (f) => f.wire_ready !== true,
);
export const MOCK_FIELD_PERMISSIONS: RoleFieldPermissionsResponse = { field_permissions: [] };

// ── Teams / Funktionsgruppen ──────────────────────────────────────────────────
export const MOCK_TEAMS: FunktionsgruppeDto[] = [
  {
    id: MOCK_TEAM_A,
    name: 'Schadenteam',
    description: 'Bearbeitet eingehende Schadenmeldungen.',
    created_at: now,
    updated_at: now,
  },
  {
    id: MOCK_GRUPPE_A,
    name: 'Innendienst',
    description: null,
    created_at: now,
    updated_at: now,
  },
];

export const MOCK_TEAM_DETAIL: Record<string, FunktionsgruppeDetailDto> = {
  [MOCK_TEAM_A]: {
    ...MOCK_TEAMS[0]!,
    members: [
      {
        user_id: MOCK_USER_B,
        display_name: 'Tomas Bianchi',
        created_at: now,
        gueltig_von: null,
        gueltig_bis: null,
      },
      // WS4 (Bereich 4 / UC8): a befristete Stellvertretung fixture, so the
      // "befristet bis …" pill has a real member to render against in mock mode.
      {
        user_id: MOCK_USER_A,
        display_name: 'Lara Meier',
        created_at: now,
        gueltig_von: '2026-08-01T00:00:00.000Z',
        gueltig_bis: '2026-09-30T00:00:00.000Z',
      },
    ],
  },
  [MOCK_GRUPPE_A]: { ...MOCK_TEAMS[1]!, members: [] },
};

// ── Kundenteams / customer-visibility teams (Berechtigungskonzept V3.0 Bereich 2, WS2) ──
const MOCK_KTEAM_SYS = 'ee000001-ee00-ee00-ee00-ee0000000001';
const MOCK_KTEAM_VIP = 'ee000002-ee00-ee00-ee00-ee0000000002';

export const MOCK_KUNDENTEAMS: KundenteamListResponse = {
  teams: [
    {
      id: MOCK_KTEAM_SYS,
      name: 'Eigene Organisation',
      description: 'Nicht loeschbares Systemteam fuer die eigene Organisation.',
      is_system_team: true,
      managed_by_role_only: true,
      member_count: 1,
      customer_count: 1,
      created_at: now,
      updated_at: now,
    },
    {
      id: MOCK_KTEAM_VIP,
      name: 'VIP-Mandate',
      description: 'Vertrauliche Mandate, nur fuer Teammitglieder sichtbar.',
      is_system_team: false,
      managed_by_role_only: false,
      member_count: 2,
      customer_count: 3,
      created_at: now,
      updated_at: now,
    },
  ],
};

export const MOCK_KUNDENTEAM_DETAIL: Record<string, KundenteamDetailDto> = {
  [MOCK_KTEAM_SYS]: {
    ...MOCK_KUNDENTEAMS.teams[0]!,
    members: [{ user_id: MOCK_USER_A, display_name: 'Lara Meier', created_at: now }],
    customers: [
      { subject_type: 'firma', subject_id: 'ff000001-ff00-ff00-ff00-ff0000000001', name: 'Muster Broker AG', created_at: now },
    ],
  },
  [MOCK_KTEAM_VIP]: {
    ...MOCK_KUNDENTEAMS.teams[1]!,
    members: [
      { user_id: MOCK_USER_A, display_name: 'Lara Meier', created_at: now },
      { user_id: MOCK_USER_B, display_name: 'Tomas Bianchi', created_at: now },
    ],
    customers: [
      { subject_type: 'firma', subject_id: 'ff000002-ff00-ff00-ff00-ff0000000002', name: 'VIP Holding AG', created_at: now },
    ],
  },
};

// ── Kontrollfunktion / control backlog (Berechtigungskonzept V3.0 Bereich 5, WS5) ──
export const MOCK_CONTROL_BACKLOG: ControlBacklogListResponse = {
  // SKELETON: placeholder Fristen → the UI banner shows.
  fristen_are_placeholder: true,
  items: [
    {
      id: 'cb000001-cb00-cb00-cb00-cb0000000001',
      element_typ: 'gerichtspost',
      element_id: 'cb0000e1-cb00-cb00-cb00-cb00000000e1',
      eingegangen_am: '2026-08-23T08:00:00Z',
      frist_bis: '2026-08-24T08:00:00Z',
      status: 'eskaliert',
      zuletzt_geprueft_am: now,
      assigned_to: null,
      assigned_to_name: null,
      assigned_group_id: null,
      assigned_group_name: null,
      subject: { subject_type: 'firma', subject_id: 'ff000002-ff00-ff00-ff00-ff0000000002', name: 'VIP Holding AG' },
      escalation_count: 1,
      created_at: '2026-08-24T08:05:00Z',
    },
    {
      id: 'cb000002-cb00-cb00-cb00-cb0000000002',
      element_typ: 'mail_stufe23',
      element_id: 'cb0000e2-cb00-cb00-cb00-cb00000000e2',
      eingegangen_am: '2026-08-21T10:00:00Z',
      frist_bis: '2026-08-23T10:00:00Z',
      status: 'offen',
      zuletzt_geprueft_am: now,
      assigned_to: null,
      assigned_to_name: null,
      assigned_group_id: null,
      assigned_group_name: null,
      subject: { subject_type: 'firma', subject_id: 'ff000001-ff00-ff00-ff00-ff0000000001', name: 'Muster Broker AG' },
      escalation_count: 0,
      created_at: '2026-08-23T10:05:00Z',
    },
  ],
};

// Berechtigungskonzept V4.0 · P7 (per-process rework) — every known process type at the shared
// defaults + the default template (mock-mode fallback for the admin config page).
export const MOCK_PROCESS_REMINDER_CONFIG: ProcessReminderConfigResponseDto = {
  process_types: PROCESS_REMINDER_PROCESS_TYPES.map((typ) => ({
    process_typ: typ,
    process_label: PROCESS_REMINDER_PROCESS_LABELS[typ],
    enabled: true,
    level_1_days: PROCESS_REMINDER_DEFAULT_LEVEL_DAYS[1],
    level_1_kanal: PROCESS_REMINDER_DEFAULT_KANAL[1],
    level_2_days: PROCESS_REMINDER_DEFAULT_LEVEL_DAYS[2],
    level_2_kanal: PROCESS_REMINDER_DEFAULT_KANAL[2],
    level_3_days: PROCESS_REMINDER_DEFAULT_LEVEL_DAYS[3],
    level_3_action: PROCESS_REMINDER_DEFAULT_L3_ACTION,
  })),
  template: { ...PROCESS_REMINDER_DEFAULT_TEMPLATE },
};

// ── Regelwerk (P2 Placement Slice 3) — the seeded code catalogs mapped to admin items ──────
const MOCK_SEED = regelwerkSeedRows();

export const MOCK_REGELWERK_SOLL: RegelwerkListResponse<RegelwerkSollDeckungItem> = {
  sprache: 'de',
  items: MOCK_SEED.rules.map((r, i) => ({
    id: `mock-rule-${i}`,
    ebene: r.ebene,
    rule_key: r.rule_key,
    branchencode: r.branchencode,
    kundentyp: r.kundentyp,
    label: pickLabel(r.label_i18n),
    label_i18n: r.label_i18n,
    pflicht_status: r.pflicht_status,
    rechtsgrundlage: r.rechtsgrundlage,
    ziel_text: pickLabel(r.ziel_text_i18n),
    ziel_text_i18n: r.ziel_text_i18n,
    kriterium_i18n: r.kriterium_i18n,
    ziel_deckungssumme: r.ziel_deckungssumme != null ? r.ziel_deckungssumme.toFixed(2) : null,
    prioritaet: r.prioritaet,
    aktiv: true,
    origin: 'system',
    customized_at: null,
    updated_at: new Date(0).toISOString(),
  })),
};

export const MOCK_REGELWERK_SCHEMA: RegelwerkListResponse<RegelwerkAttributSchemaItem> = {
  sprache: 'de',
  items: MOCK_SEED.schema.map((s, i) => ({
    id: `mock-schema-${i}`,
    ebene: s.ebene,
    branchencode: s.branchencode,
    profil_typ: s.profil_typ,
    kategorie: s.kategorie,
    feld_key: s.feld_key,
    typ: s.typ,
    label: pickLabel(s.label_i18n),
    label_i18n: s.label_i18n,
    options_json: s.options_json,
    einheit: s.einheit,
    hinweis_i18n: s.hinweis_i18n,
    pflicht: false,
    sort_order: s.sort_order,
    aktiv: true,
    origin: 'system',
    customized_at: null,
    updated_at: new Date(0).toISOString(),
  })),
};

// ── Tolerance config ──────────────────────────────────────────────────────────
export const MOCK_TOLERANCE: ToleranceConfigEntryDto[] = [
  {
    line_of_business: null,
    mode: 'percent',
    rounding_tolerance: '0.50',
    t1: '2.00',
    t2: '5.00',
    valid_from: null,
    version: null,
    is_default: true,
  },
  {
    line_of_business: 'uvg',
    mode: 'absolute',
    rounding_tolerance: '1.00',
    t1: '25.00',
    t2: '100.00',
    valid_from: '2026-01-01',
    version: 3,
    is_default: false,
  },
];

// ── Channels ──────────────────────────────────────────────────────────────────
export const MOCK_CHANNELS: ChannelConfigDto[] = CHANNEL_CATALOG.slice(0, 5).map((c, i) => ({
  channel_key: c.key as ChannelConfigDto['channel_key'],
  label: c.label,
  is_eingang: c.eingang,
  is_zustellung: c.zustellung,
  is_benachrichtigung: c.benachrichtigung,
  active: i !== 4,
  priority: c.zustellung ? i + 1 : null,
  fallback_channel: null,
  default_sender: null,
  default_recipient: null,
  retention_note: null,
  manage_permission: null,
  version: null,
  is_default: true,
  updated_at: null,
}));

export const MOCK_CHANNEL_MATRIX: ChannelMatrixEntryDto[] = [
  {
    id: 'aa000030-0000-0000-0000-000000000030',
    doc_type: 'police' as ChannelMatrixEntryDto['doc_type'],
    product: null,
    channel_key: MOCK_CHANNELS[0]!.channel_key,
    version: 1,
    updated_at: now,
  },
];

// ── Routing ───────────────────────────────────────────────────────────────────
export const MOCK_CONFIDENCE: ConfidencePolicyEntryDto[] = [
  { process: 'email_intake', auto_threshold: '0.90', freigabe_threshold: '0.60', is_default: true },
  { process: 'invoice_check', auto_threshold: '0.95', freigabe_threshold: '0.70', is_default: false },
  { process: 'zefix_enrich', auto_threshold: '0.85', freigabe_threshold: '0.50', is_default: true },
  // P4-S5: the Unterschriften-Ähnlichkeitssuche surface reads this mandate_signing row (0.90/0.70).
  { process: 'mandate_signing', auto_threshold: '0.90', freigabe_threshold: '0.70', is_default: true },
];

export const MOCK_GROUP_ROUTING: GroupRoutingEntryDto[] = [
  { process: 'invoice_check', funktionsgruppe_id: MOCK_TEAM_A },
  { process: 'email_intake', funktionsgruppe_id: null },
];

// ── Mandant + Portal ──────────────────────────────────────────────────────────
export const MOCK_TENANT: TenantConfigDto = {
  id: 'aa000099-0000-0000-0000-000000000099',
  name: 'Demo Brokerfirma',
  slug: 'demo-brokerfirma',
  own_domains: ['brokerfirma-beispiel.ch'],
  mail_default_team_id: null,
  mail_default_team_name: null,
};

export const MOCK_PORTAL_CONFIG: PortalConfigDto = {
  enabled: true,
  branding: { logoRef: null, primaryHex: '#1b7a5e', secondaryHex: null },
  feature_toggles: Object.fromEntries(PORTAL_FEATURE_KEYS.map((k) => [k, k === 'policen' || k === 'rechnungen'])) as PortalConfigDto['feature_toggles'],
  invite_criteria: { kundenStatuses: ['aktiv'], requireEmail: true },
  reminder_cascade: [10, 20, 30],
  access_retention_days: 90,
  activation_link_days: 30,
  version: 2,
  is_default: false,
  updated_at: '2026-05-01T00:00:00.000Z',
};

/** The primary holder (portal_access): invited, not yet activated. */
export const MOCK_PORTAL_ACCESS: PortalAccessResponse = {
  access: {
    id: 'aa000041-0000-0000-0000-000000000041',
    subject_type: 'firma',
    subject_id: 'cust-1',
    status: 'invited',
    invite_email: 'kunde@beispiel.ch',
    invited_at: '2026-04-01T10:00:00.000Z',
    activated_at: null,
  },
};

export const MOCK_PORTAL_GRANTS: PortalGrantAdminItem[] = [
  {
    id: 'aa000040-0000-0000-0000-000000000040',
    role_label: 'treuhaender',
    role: null,
    scopes: ['documents.read'],
    status: 'active',
    invited_at: '2026-04-01T10:00:00.000Z',
    activated_at: '2026-04-02T10:00:00.000Z',
    revoked_at: null,
    created_at: '2026-04-01T10:00:00.000Z',
  },
];

// ── AVB ───────────────────────────────────────────────────────────────────────
export const MOCK_AVB: AvbDocumentResponse[] = [
  {
    id: 'aa000050-0000-0000-0000-000000000050',
    carrier_uid: 'CHE-123.456.789',
    carrier_name: 'Beispiel Versicherung AG',
    product_label: 'Betriebshaftpflicht',
    line_of_business: 'haftpflicht',
    title: 'AVB Betriebshaftpflicht',
    version_label: 'Ausgabe 2026',
    valid_from: '2026-01-01',
    valid_to: null,
    source: 'manual',
    filename: 'avb-bhv-2026.pdf',
    mime_type: 'application/pdf',
    size_bytes: 428_000,
    created_at: '2026-01-05T09:00:00.000Z',
  },
];

// ── Audit ─────────────────────────────────────────────────────────────────────
export const MOCK_AUDIT: AuditListResponse = {
  data: [
    {
      id: '1024',
      actor_kind: 'human',
      user_id: MOCK_USER_A,
      action: 'UPDATE',
      entity_type: 'ROLE',
      entity_id: MOCK_ROLE_FINANCE,
      before: null,
      after: null,
      context: null,
      severity: 'high',
      created_at: '2026-05-10T12:30:00.000Z',
    },
    {
      id: '1023',
      actor_kind: 'system',
      user_id: null,
      action: 'CREATE',
      entity_type: 'TENANT',
      entity_id: MOCK_TENANT.id,
      before: null,
      after: null,
      context: null,
      severity: 'normal',
      created_at: '2026-05-09T08:00:00.000Z',
    },
  ],
  page: { next_cursor: null, limit: 50 },
};

// ── Health ────────────────────────────────────────────────────────────────────
export const MOCK_HEALTH: HealthStatus = { status: 'ok', version: 'dev' };

// ── Mahnwesen P-04 / P-05 (Analysemandat, Brokermandat) ─────────────────────────────
// The seed = Stefan's defaults per process; used_default true until the tenant saves once.
export function mockMahnwesenConfig(processKey: 'P-04' | 'P-05'): MahnwesenProcessConfigDto {
  return {
    process_key: processKey,
    ...mahnwesenProcessDefaults(processKey),
    used_default: true,
    updated_at: null,
    optional_third_frist_day: MAHNWESEN_OPTIONAL_THIRD_FRIST_DAY,
  };
}
export const MOCK_MAHNWESEN_CONFIG: MahnwesenProcessConfigDto = mockMahnwesenConfig('P-04');

// The effective text list of a process: every EMAIL entry of its matrix, default source.
export function mockTextTemplates(processKey: 'P-04' | 'P-05' | 'P-06'): TextTemplateDto[] {
  return textTemplateListMatrix(processKey).flatMap(({ step, segments }) =>
    (['sie', 'du'] as const).flatMap((anrede) =>
      segments.map((segment) => {
        const key = { process_key: processKey, step_key: step, kanal: 'EMAIL' as const, lang: 'de' as const, anrede, segment };
        const def = resolveTextTemplateDefault(key);
        return {
          ...key,
          subject: def?.subject ?? '',
          body: def?.body ?? '',
          source: 'default' as const,
          is_active: true,
          updated_at: null,
        };
      }),
    ),
  );
}
export const MOCK_TEXT_TEMPLATES: TextTemplateDto[] = mockTextTemplates('P-04');
